/**
 * SuggestionsPage
 *
 * Super admin page for viewing and managing platform-level suggestions.
 * Shows suggestions that require global changes (prompt modifications, bug fixes, etc.)
 *
 * Part of Story 30: Domain Knowledge Refinement System
 *
 * Date: December 19, 2025
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import {
  MessageSquare,
  Bug,
  Wrench,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Loader2,
  ExternalLink,
  ChevronRight,
} from 'lucide-react';
import { format } from 'date-fns';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';

interface Suggestion {
  id: string;
  siteId: string;
  siteName: string;
  suggestionType: string;
  title: string;
  description: string;
  originalIssue: {
    type?: string;
    severity?: string;
    description?: string;
    location?: string;
    suggestion?: string;
  };
  contentType: string;
  contentId: string;
  status: string;
  priority: number;
  submittedBy: string;
  submittedByName: string;
  submittedAt: string;
  createdAt: string;
}

const typeIcons: Record<string, React.ReactNode> = {
  TERMINOLOGY_FIX: <MessageSquare className="h-4 w-4" />,
  CONTEXT_ADDITION: <MessageSquare className="h-4 w-4" />,
  DISPLAY_FIX: <Wrench className="h-4 w-4" />,
  SYSTEM_BUG: <Bug className="h-4 w-4" />,
  GENERAL: <MessageSquare className="h-4 w-4" />,
};

const typeLabels: Record<string, string> = {
  TERMINOLOGY_FIX: 'Terminology Fix',
  CONTEXT_ADDITION: 'Add Context',
  DISPLAY_FIX: 'Display Fix',
  SYSTEM_BUG: 'System Bug',
  GENERAL: 'General',
};

const typeColors: Record<string, string> = {
  TERMINOLOGY_FIX: 'bg-blue-100 text-blue-800',
  CONTEXT_ADDITION: 'bg-green-100 text-green-800',
  DISPLAY_FIX: 'bg-yellow-100 text-yellow-800',
  SYSTEM_BUG: 'bg-red-100 text-red-800',
  GENERAL: 'bg-gray-100 text-gray-800',
};

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  reviewed: 'bg-blue-100 text-blue-800',
  implemented: 'bg-green-100 text-green-800',
  dismissed: 'bg-gray-100 text-gray-800',
};

export default function SuggestionsPage() {
  const { selectedSite } = useSite();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveStatus, setResolveStatus] = useState<'implemented' | 'dismissed'>('implemented');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (selectedSite?.id) {
      loadSuggestions();
    }
  }, [selectedSite?.id, filter]);

  const loadSuggestions = async () => {
    if (!selectedSite?.id) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({ siteId: selectedSite.id });
      if (filter !== 'all') {
        params.append('suggestionType', filter);
      }

      const response = await authFetch(`/content-evaluations/suggestions?${params}`);
      const data = await response.json();

      if (data.success) {
        setSuggestions(data.data || []);
      }
    } catch (err) {
      console.error('[SuggestionsPage] Error loading suggestions:', err);
      toast({
        title: 'Error',
        description: 'Failed to load suggestions',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedSuggestion) return;

    setResolving(true);
    try {
      const response = await authFetch(`/content-evaluations/suggestions/${selectedSuggestion.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: resolveStatus,
          resolutionNotes,
        }),
      });

      const data = await response.json();
      if (data.success) {
        toast({
          title: 'Success',
          description: `Suggestion marked as ${resolveStatus}`,
        });
        setResolveDialogOpen(false);
        setSelectedSuggestion(null);
        setResolutionNotes('');
        loadSuggestions();
      }
    } catch (err) {
      console.error('[SuggestionsPage] Error resolving suggestion:', err);
      toast({
        title: 'Error',
        description: 'Failed to resolve suggestion',
        variant: 'destructive',
      });
    } finally {
      setResolving(false);
    }
  };

  const pendingCount = suggestions.filter(s => s.status === 'pending').length;
  const bugCount = suggestions.filter(s => s.suggestionType === 'SYSTEM_BUG').length;

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Platform Suggestions</h1>
          <p className="text-muted-foreground">
            Review and manage suggestions for global prompt and system improvements
          </p>
        </div>
        <div className="flex items-center gap-4">
          {bugCount > 0 && (
            <Badge variant="destructive" className="gap-1">
              <Bug className="h-3 w-3" />
              {bugCount} Bug{bugCount !== 1 ? 's' : ''} to review
            </Badge>
          )}
          <Badge variant="outline" className="gap-1">
            <Clock className="h-3 w-3" />
            {pendingCount} Pending
          </Badge>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="TERMINOLOGY_FIX">Terminology Fixes</SelectItem>
            <SelectItem value="CONTEXT_ADDITION">Context Additions</SelectItem>
            <SelectItem value="DISPLAY_FIX">Display Fixes</SelectItem>
            <SelectItem value="SYSTEM_BUG">System Bugs</SelectItem>
            <SelectItem value="GENERAL">General</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : suggestions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
            <h3 className="text-lg font-medium">All caught up!</h3>
            <p className="text-muted-foreground">No pending suggestions to review.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {suggestions.map((suggestion) => (
            <Card
              key={suggestion.id}
              className={`hover:shadow-md transition-shadow ${
                suggestion.suggestionType === 'SYSTEM_BUG' ? 'border-red-200' : ''
              }`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${typeColors[suggestion.suggestionType] || 'bg-gray-100'}`}>
                      {typeIcons[suggestion.suggestionType] || <MessageSquare className="h-4 w-4" />}
                    </div>
                    <div>
                      <CardTitle className="text-base">{suggestion.title}</CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {typeLabels[suggestion.suggestionType] || suggestion.suggestionType}
                        </Badge>
                        <span className="text-xs">
                          from {suggestion.siteName}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(suggestion.createdAt), 'MMM d, yyyy')}
                        </span>
                      </CardDescription>
                    </div>
                  </div>
                  <Badge className={statusColors[suggestion.status]}>
                    {suggestion.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  {suggestion.description}
                </p>

                {suggestion.originalIssue && (
                  <div className="bg-muted/50 rounded-lg p-3 mb-4">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Original Issue:</p>
                    <p className="text-sm">{suggestion.originalIssue.description}</p>
                    {suggestion.originalIssue.location && (
                      <p className="text-xs text-muted-foreground mt-1 italic">
                        Location: "{suggestion.originalIssue.location}"
                      </p>
                    )}
                    {suggestion.originalIssue.suggestion && (
                      <p className="text-sm text-blue-600 mt-2">
                        Suggested: {suggestion.originalIssue.suggestion}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground">
                    Submitted by {suggestion.submittedByName || 'Unknown'}
                  </div>
                  <div className="flex items-center gap-2">
                    {suggestion.contentId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`/content-review?id=${suggestion.contentId}&type=${suggestion.contentType}`, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View Content
                      </Button>
                    )}
                    {suggestion.status === 'pending' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedSuggestion(suggestion);
                            setResolveStatus('dismissed');
                            setResolveDialogOpen(true);
                          }}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Dismiss
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedSuggestion(suggestion);
                            setResolveStatus('implemented');
                            setResolveDialogOpen(true);
                          }}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Mark Implemented
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Resolve Dialog */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {resolveStatus === 'implemented' ? 'Mark as Implemented' : 'Dismiss Suggestion'}
            </DialogTitle>
            <DialogDescription>
              {resolveStatus === 'implemented'
                ? 'Confirm that you have implemented this suggestion.'
                : 'Explain why this suggestion is being dismissed.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="notes">
                {resolveStatus === 'implemented' ? 'Implementation Notes' : 'Dismissal Reason'}
              </Label>
              <Textarea
                id="notes"
                placeholder={
                  resolveStatus === 'implemented'
                    ? 'Describe what changes were made...'
                    : 'Explain why this suggestion was not implemented...'
                }
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleResolve}
              disabled={resolving}
              variant={resolveStatus === 'dismissed' ? 'destructive' : 'default'}
            >
              {resolving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {resolveStatus === 'implemented' ? 'Confirm Implementation' : 'Dismiss'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
